// Treatment Wizard rules: pure logic, no DOM. Mirrors the CSV routing rules (first match wins).
import {
    AREAS,
    BMI_LIMITS,
    RESULTS,
    MULTIPLE_OPTIONS,
    MODIFIER_MESSAGES,
    SKIN_EXCISION_360_NOTE,
    WEIGHT_CHANGE_LABELS,
} from './config';

const AREA_LABELS = Object.fromEntries(AREAS.map((a) => [a.id, a.label]));

// BMI = 703 × lb ÷ in². Returns the UNROUNDED value (compare limits against this), or null.
export function calcBmi(feet, inches, pounds) {
    const totalInches = Number(feet) * 12 + Number(inches);
    if (!(totalInches > 0) || !(Number(pounds) > 0)) return null;
    return (703 * Number(pounds)) / (totalInches * totalInches);
}

export const formatBmi = (bmi) => (bmi === null ? '' : bmi.toFixed(1));

export function validateMeasurements({ heightFeet, heightInches, weight }) {
    const f = Number(heightFeet);
    const i = Number(heightInches);
    const w = Number(weight);
    return (
        String(heightFeet).trim() !== '' &&
        String(heightInches).trim() !== '' &&
        String(weight).trim() !== '' &&
        Number.isInteger(f) &&
        f > 0 &&
        Number.isInteger(i) &&
        i >= 0 &&
        i <= 11 &&
        Number.isFinite(w) &&
        w > 0
    );
}

// Abdomen + 360 midsection: abdomen is part of the 360, so don't count it twice.
export function dedupeAreas(areas) {
    return areas.includes('midsection') ? areas.filter((a) => a !== 'belly') : [...areas];
}

export const needsPriority = (answers) => dedupeAreas(answers.areas).length > 1;

export function topArea(answers) {
    const areas = dedupeAreas(answers.areas);
    if (areas.length === 1) return areas[0];
    return areas.includes(answers.priority) ? answers.priority : null;
}

const pathOf = (description) => ({ doughy: 'fat', loose: 'skin', both: 'both' })[description] || null;

// 360 midsection + skin/both path: ask about abdominal loose skin before skin excision can be suggested.
export function needsAbdomenQuestion(answers) {
    const bmi = calcBmi(answers.heightFeet, answers.heightInches, answers.weight);
    return (
        topArea(answers) === 'midsection' &&
        !answers.areas.includes('other') &&
        (answers.description === 'loose' || answers.description === 'both') &&
        bmi !== null &&
        bmi <= BMI_LIMITS.skinExcision
    );
}

const REQUIRED = ['concern', 'description', 'downtime', 'change', 'budget', 'weightChange'];

export function isComplete(answers) {
    if (!answers.areas.length) return false;
    if (needsPriority(answers) && !topArea(answers)) return false;
    if (!validateMeasurements(answers)) return false;
    return REQUIRED.every((k) => !!answers[k]);
}

// Which treatments are eligible for an education path (BMI limits + area rules).
export function matchTreatments(answers, bmi) {
    const path = pathOf(answers.description);
    if (!path) return [];
    const top = topArea(answers);
    const out = [];
    // T-Shape 2 and awake lipo: all six listed areas ("Other" is routed to clarification earlier).
    if (bmi <= BMI_LIMITS.tshape) out.push('TSHAPE');
    if (bmi <= BMI_LIMITS.lipo && (path === 'fat' || path === 'both')) out.push('LIPO');
    if (bmi <= BMI_LIMITS.skinExcision && (path === 'skin' || path === 'both')) {
        const areaOk = top === 'belly' || top === 'arms' || (top === 'midsection' && answers.abdomenSkin === 'yes');
        if (areaOk) out.push('SKIN_EXCISION');
    }
    return out;
}

// Main routing — first matching rule wins. Returns { ruleId, resultKey } or { invalid: true }.
export function route(answers) {
    if (!isComplete(answers)) return { ruleId: 'INVALID_INPUT', invalid: true };
    const bmi = calcBmi(answers.heightFeet, answers.heightInches, answers.weight);
    const { concern, description } = answers;

    if (bmi > BMI_LIMITS.lipo) return { ruleId: 'BMI_ABOVE_40', resultKey: 'CONSULT_BMI' };
    if (description === 'unsure' || answers.areas.includes('other'))
        return { ruleId: 'UNCLEAR', resultKey: 'CONSULT_CLARIFY_UNSURE' };
    if (
        (concern === 'fat' && description === 'loose') ||
        (concern === 'skin' && description === 'doughy')
    )
        return { ruleId: 'CONFLICT', resultKey: 'CONSULT_CLARIFY_CONFLICT' };
    if (description === 'firm') return { ruleId: 'FIRM_BELLY', resultKey: 'CONSULT_ASSESS' };
    if (answers.change === 'major' && (answers.downtime === 'none' || answers.budget === 'low'))
        return { ruleId: 'EXPECTATIONS', resultKey: 'CONSULT_EXPECTATIONS' };
    if (description === 'doughy') return { ruleId: 'FAT', resultKey: 'EDUCATION_FAT' };
    if (description === 'loose') return { ruleId: 'SKIN', resultKey: 'EDUCATION_SKIN' };
    if (description === 'both') return { ruleId: 'BOTH', resultKey: 'EDUCATION_COMBINED' };
    return { ruleId: 'DEFAULT', resultKey: 'CONSULT_GENERAL' };
}

// Full result model for the UI.
export function buildResult(answers) {
    const routed = route(answers);
    if (routed.invalid) return { invalid: true };

    const bmi = calcBmi(answers.heightFeet, answers.heightInches, answers.weight);
    let { ruleId, resultKey } = routed;
    let treatments = [];
    let showCombined = false;

    if (resultKey.startsWith('EDUCATION_')) {
        treatments = matchTreatments(answers, bmi);
        if (!treatments.length) {
            // No clear fit → general consultation guidance.
            ruleId = 'DEFAULT';
            resultKey = 'CONSULT_GENERAL';
        } else {
            showCombined = answers.description === 'both';
        }
    }

    const base = RESULTS[resultKey];
    let headline = base.headline;
    let message = base.message;
    let button = base.button;
    const multiple = treatments.length >= 2;
    if (multiple) {
        headline = MULTIPLE_OPTIONS.headline;
        message = treatments.length === 3 ? MULTIPLE_OPTIONS.allThree : MULTIPLE_OPTIONS.intro;
        button = MULTIPLE_OPTIONS.button;
    }

    // Supporting notes (modifiers). BMI note is skipped on the BMI result, which already says it.
    const notes = [];
    if (ruleId !== 'BMI_ABOVE_40') {
        notes.push(
            bmi <= BMI_LIMITS.tshape
                ? MODIFIER_MESSAGES.BMI_30_OR_LESS
                : bmi <= BMI_LIMITS.lipo
                  ? MODIFIER_MESSAGES.BMI_30_TO_40
                  : MODIFIER_MESSAGES.BMI_OVER_40
        );
    }
    if (treatments.includes('SKIN_EXCISION')) {
        notes.push(MODIFIER_MESSAGES.SKIN_EXCISION);
        if (topArea(answers) === 'midsection') notes.push(SKIN_EXCISION_360_NOTE);
    }
    notes.push(MODIFIER_MESSAGES.downtime[answers.downtime]);
    notes.push(MODIFIER_MESSAGES.change[answers.change]);
    notes.push(MODIFIER_MESSAGES.budget[answers.budget]);

    return {
        ruleId,
        resultKey,
        headline,
        message,
        button,
        treatments,
        showCombined,
        multiple,
        notes: notes.filter(Boolean),
        summary: {
            areas: dedupeAreas(answers.areas).map((a) => AREA_LABELS[a]),
            bmi: formatBmi(bmi),
            weightChange: WEIGHT_CHANGE_LABELS[answers.weightChange],
        },
    };
}
